import { computed, inject, Injectable, signal } from "@angular/core";
import { DevicesResponse, LicensesResponse, ResetPasswordPayload, ShowResponse } from "../../Interfaces/api/Intune/users.interface";
import { httpResource } from "@angular/common/http";
import { Observable } from "rxjs";
import { UsersService } from "../../Services/Intune/users.service";
import { IntuneDevice } from "../../Models/Intune/intune-device.model";
import { IntuneUser, LicenseDetails } from "../../Models/Intune/intune-user.model";
import { NoContentResponse } from "../../Modules/Shared/Interfaces/response.interface";
import { DEVICE_SELECT_FIELDS } from "../../Consts/Intune/show-user.const";
import { ResourceStateService } from "../../Modules/Shared/Services/resource-state.service";


@Injectable()
export class ShowUserFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _usersService = inject(UsersService);

    private readonly _selectedUserId = signal<string | null>(null);

    private readonly _showResource = httpResource<ShowResponse>(() => {
        const userId = this._selectedUserId();
        if (!userId) return undefined;
        return this._usersService.show(userId);
    });

    private readonly _devicesResource = httpResource<DevicesResponse>(() => {
        const userId = this._selectedUserId();
        if (!userId) return undefined;
        return this._usersService.devices(userId, DEVICE_SELECT_FIELDS);
    });

    private readonly _licensesResource = httpResource<LicensesResponse>(() => {
        const userId = this._selectedUserId();
        if (!userId) return undefined;
        return this._usersService.licenses(userId);
    });

    // ACTIONS
    setUserId(userId: string | null) {
        if (this._selectedUserId() === userId) {
            if (userId) {
                this._showResource.reload();
                this._devicesResource.reload();
                this._licensesResource.reload();
            }
        } else {
            this._selectedUserId.set(userId);
        }
    }

    resetPassword(payload: ResetPasswordPayload): Observable<NoContentResponse> {
        const id = this._selectedUserId();
        if (!id) throw new Error('No user selected');
        return this._usersService.resetPassword(id, payload);
    }

    // PUBLIC READ MODELS
    readonly user = computed(() => this._resourceState.valueOf(this._showResource)?.data ? new IntuneUser(this._resourceState.valueOf(this._showResource)?.data) : null);
    readonly userStatus = computed(() => this._showResource.status());
    readonly userError = computed(() => this._showResource.error());

    // DEVICES
    readonly devices = computed<IntuneDevice[]>(() => {
        const raw = this._resourceState.valueOf(this._devicesResource)?.data?.value as unknown;
        const value = Array.isArray(raw) ? raw as Record<string, unknown>[] : [];
        return value.map((d) => new IntuneDevice(d as Partial<IntuneDevice>));
    });
    readonly devicesStatus = computed(() => this._devicesResource.status());
    readonly devicesError = computed(() => this._devicesResource.error());

    // LICENSES
    readonly licenses = computed<LicenseDetails[]>(() => {
        const raw = this._resourceState.valueOf(this._licensesResource)?.data?.value as unknown;
        const value = Array.isArray(raw) ? raw as Record<string, unknown>[] : [];
        return value.map((d) => new LicenseDetails(d as Partial<LicenseDetails>));
    });
    readonly licensesStatus = computed(() => this._licensesResource.status());
    readonly licensesError = computed(() => this._licensesResource.error());
}